/**
 * Web Audio API — beeps de feedback para Terminal Kiosk.
 *
 * Sem arquivos MP3 (Bloco F.9 D11). Oscillator simples gerado on-the-fly.
 *   - OK: beep curto agudo (880Hz, 80ms)
 *   - ERRO: beep duplo grave (200Hz, 150ms x2)
 *   - AVISO: beep medio (440Hz, 100ms)
 *
 * Browser modern requer interacao do usuario antes de AudioContext tocar
 * (auto-play policy). Como o terminal eh sempre operado por click, isso
 * nao eh problema na pratica.
 */

import { useCallback, useRef } from "react";

function _criarContext() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    return new AC();
  } catch {
    return null;
  }
}

function _beep(ctx, { freq, durationMs, volume = 0.2 }) {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.value = volume;
  osc.connect(gain);
  gain.connect(ctx.destination);
  const now = ctx.currentTime;
  osc.start(now);
  osc.stop(now + durationMs / 1000);
  // Fade rapido pra evitar click no final
  gain.gain.setValueAtTime(volume, now);
  gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
}

export function useSomFeedback() {
  const ctxRef = useRef(null);

  const ensure = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = _criarContext();
    }
    // resume se estiver suspended (Safari/iOS)
    if (ctxRef.current && ctxRef.current.state === "suspended") {
      try {
        ctxRef.current.resume();
      } catch {
        /* ignore */
      }
    }
    return ctxRef.current;
  }, []);

  const tocarOk = useCallback(() => {
    const ctx = ensure();
    _beep(ctx, { freq: 880, durationMs: 80 });
  }, [ensure]);

  const tocarErro = useCallback(() => {
    const ctx = ensure();
    if (!ctx) return;
    _beep(ctx, { freq: 200, durationMs: 150 });
    setTimeout(() => _beep(ctx, { freq: 200, durationMs: 150 }), 200);
  }, [ensure]);

  const tocarAviso = useCallback(() => {
    const ctx = ensure();
    _beep(ctx, { freq: 440, durationMs: 100 });
  }, [ensure]);

  return { tocarOk, tocarErro, tocarAviso };
}
